
/**
 * do not need review have not yet been integrated
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Paperclip,
  Mic,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock3,
  Plus,
  X,
  Check,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { motion, AnimatePresence } from 'framer-motion'
import { NewChatView } from '@/components/chat/NewChatView'
import { ArtifactCard } from '@/components/chat/ArtifactCard'
import type { InterviewMessage, ContextGap } from '@/types/api'
import type { ArtifactGeneration } from '@/hooks/useArtifactGeneration'

export interface ChatAreaProps {
  messages: InterviewMessage[]
  gaps?: ContextGap[]
  inputValue: string
  loading: boolean
  agentState: 'thinking' | 'extracting' | 'idle'
  scrollRef: React.RefObject<HTMLDivElement>
  onInputChange: (v: string) => void
  onSend: (text?: string) => void
  onSelectCategory: (cat: string) => void
  onUploadClick?: () => void
  voiceCaptureRequestId?: number
  artifactGenerations?: ArtifactGeneration[]
  onDismissArtifact?: (artifactId: string) => void
}

type VoiceState = 'idle' | 'recording' | 'processing'

const VOICE_TRANSCRIBE_DELAY_MS = 1300
const VOICE_STOP_FALLBACK_MS = 900
const WAVEFORM_BARS = [10, 14, 22, 28, 18, 24, 30, 20, 16, 12, 18, 26, 20, 14, 10]

interface SpeechRecognitionAlternativeLike {
  transcript: string
}

interface SpeechRecognitionResultLike {
  isFinal: boolean
  length: number
  [index: number]: SpeechRecognitionAlternativeLike
}

interface SpeechRecognitionEventLike extends Event {
  resultIndex: number
  results: ArrayLike<SpeechRecognitionResultLike>
}

interface SpeechRecognitionErrorEventLike extends Event {
  error?: string
}

interface SpeechRecognitionLike {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives?: number
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null
  onend: ((event: Event) => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike

const getSpeechRecognitionCtor = (): SpeechRecognitionCtor | null => {
  const host = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return host.SpeechRecognition ?? host.webkitSpeechRecognition ?? null
}

const speechErrorToMessage = (error?: string): string => {
  switch (error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone permission was denied. Please allow microphone access and try again.'
    case 'audio-capture':
      return 'No microphone was detected. Please connect one and retry.'
    case 'network':
      return 'Transcription network error. Please retry.'
    case 'no-speech':
      return 'No speech detected. Please try again.'
    default:
      return 'System transcription is unavailable right now. Please try again.'
  }
}

export function ChatArea({
  messages,
  gaps,
  inputValue,
  loading,
  agentState,
  scrollRef,
  onInputChange,
  onSend,
  onSelectCategory,
  onUploadClick,
  voiceCaptureRequestId = 0,
  artifactGenerations,
  onDismissArtifact,
}: ChatAreaProps) {
  const [voiceState, setVoiceState] = useState<VoiceState>('idle')
  const [voiceError, setVoiceError] = useState<string | null>(null)
  const [isComposerExpanded, setIsComposerExpanded] = useState(false)
  const transcriptionTimerRef = useRef<number | null>(null)
  const stopFallbackTimerRef = useRef<number | null>(null)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const finalTranscriptRef = useRef('')
  const interimTranscriptRef = useRef('')
  const pendingTranscriptionRef = useRef(false)
  const finalizeTriggeredRef = useRef(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const clearTranscriptionTimer = useCallback(() => {
    if (transcriptionTimerRef.current) {
      window.clearTimeout(transcriptionTimerRef.current)
      transcriptionTimerRef.current = null
    }
  }, [])

  const clearStopFallbackTimer = useCallback(() => {
    if (stopFallbackTimerRef.current) {
      window.clearTimeout(stopFallbackTimerRef.current)
      stopFallbackTimerRef.current = null
    }
  }, [])

  const clearTranscriptBuffers = useCallback(() => {
    finalTranscriptRef.current = ''
    interimTranscriptRef.current = ''
  }, [])

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    textarea.style.height = 'auto'
    const maxHeight = 300
    const nextHeight = Math.min(textarea.scrollHeight, maxHeight)
    textarea.style.height = `${Math.max(24, nextHeight)}px`
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden'
    setIsComposerExpanded(nextHeight > 34 || inputValue.trim().length > 110)
  }, [inputValue])

  const getCombinedTranscript = useCallback(
    () => `${finalTranscriptRef.current} ${interimTranscriptRef.current}`.trim(),
    []
  )

  const finalizeTranscript = useCallback(() => {
    if (finalizeTriggeredRef.current) return
    finalizeTriggeredRef.current = true
    clearStopFallbackTimer()

    const nextTranscript = getCombinedTranscript()
    clearTranscriptionTimer()
    transcriptionTimerRef.current = window.setTimeout(() => {
      if (nextTranscript.length > 0) {
        onInputChange(nextTranscript)
        requestAnimationFrame(() => textareaRef.current?.focus())
      } else {
        setVoiceError('No speech was captured. Please try again.')
      }
      clearTranscriptBuffers()
      pendingTranscriptionRef.current = false
      finalizeTriggeredRef.current = false
      setVoiceState('idle')
      transcriptionTimerRef.current = null
    }, VOICE_TRANSCRIBE_DELAY_MS)
  }, [
    clearStopFallbackTimer,
    clearTranscriptBuffers,
    clearTranscriptionTimer,
    getCombinedTranscript,
    onInputChange,
  ])

  const startVoiceCapture = useCallback(() => {
    clearTranscriptionTimer()
    clearStopFallbackTimer()
    clearTranscriptBuffers()
    pendingTranscriptionRef.current = false
    finalizeTriggeredRef.current = false
    setVoiceError(null)

    const SpeechRecognitionCtor = getSpeechRecognitionCtor()
    if (!SpeechRecognitionCtor) {
      setVoiceState('idle')
      setVoiceError('System transcription is not supported in this browser.')
      return
    }

    if (!recognitionRef.current) {
      const recognition = new SpeechRecognitionCtor()
      recognition.continuous = true
      recognition.interimResults = true
      recognition.lang = 'en-US'
      recognition.maxAlternatives = 1
      recognition.onresult = (event: SpeechRecognitionEventLike) => {
        let nextFinal = finalTranscriptRef.current
        let nextInterim = ''
        for (let idx = event.resultIndex; idx < event.results.length; idx += 1) {
          const result = event.results[idx]
          const candidate = result[0]?.transcript?.trim() ?? ''
          if (!candidate) continue
          if (result.isFinal) {
            nextFinal = `${nextFinal} ${candidate}`.trim()
          } else {
            nextInterim = `${nextInterim} ${candidate}`.trim()
          }
        }
        finalTranscriptRef.current = nextFinal
        interimTranscriptRef.current = nextInterim
      }
      recognition.onerror = (event: SpeechRecognitionErrorEventLike) => {
        if (pendingTranscriptionRef.current) return
        setVoiceState('idle')
        setVoiceError(speechErrorToMessage(event.error))
      }
      recognition.onend = () => {
        if (pendingTranscriptionRef.current) {
          finalizeTranscript()
        }
      }
      recognitionRef.current = recognition
    }

    try {
      recognitionRef.current.start()
      setVoiceState('recording')
    } catch {
      setVoiceState('idle')
      setVoiceError('Could not start microphone capture. Please try again.')
    }
  }, [clearStopFallbackTimer, clearTranscriptBuffers, clearTranscriptionTimer, finalizeTranscript])

  const cancelVoiceCapture = useCallback(() => {
    clearTranscriptionTimer()
    clearStopFallbackTimer()
    pendingTranscriptionRef.current = false
    finalizeTriggeredRef.current = false
    clearTranscriptBuffers()
    setVoiceError(null)
    setVoiceState('idle')
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort()
      } catch {
        // noop
      }
    }
  }, [clearStopFallbackTimer, clearTranscriptBuffers, clearTranscriptionTimer])

  const completeVoiceCapture = useCallback(() => {
    if (voiceState !== 'recording') return
    setVoiceState('processing')
    setVoiceError(null)
    clearTranscriptionTimer()
    clearStopFallbackTimer()
    pendingTranscriptionRef.current = true
    finalizeTriggeredRef.current = false
    stopFallbackTimerRef.current = window.setTimeout(() => {
      finalizeTranscript()
    }, VOICE_STOP_FALLBACK_MS)

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop()
        return
      } catch {
        // fall through to finalize immediately if stop fails
      }
    }
    finalizeTranscript()
  }, [clearStopFallbackTimer, clearTranscriptionTimer, finalizeTranscript, voiceState])

  useEffect(() => {
    return () => {
      clearTranscriptionTimer()
      clearStopFallbackTimer()
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort()
        } catch {
          // noop
        }
        recognitionRef.current.onresult = null
        recognitionRef.current.onerror = null
        recognitionRef.current.onend = null
      }
    }
  }, [clearStopFallbackTimer, clearTranscriptionTimer])

  useEffect(() => {
    if (!voiceCaptureRequestId) return
    startVoiceCapture()
  }, [voiceCaptureRequestId, startVoiceCapture])

  useEffect(() => {
    if (voiceState !== 'idle') return
    resizeTextarea()
  }, [inputValue, resizeTextarea, voiceState])

  const priorityRank: Record<ContextGap['priority'], number> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
  }
  const unresolvedGaps = (gaps ?? []).filter((gap) => gap.status !== 'resolved')
  const sortedOpenGaps = [...unresolvedGaps].sort(
    (a, b) => priorityRank[a.priority] - priorityRank[b.priority]
  )
  const visibleGaps = sortedOpenGaps.slice(0, 3)

  const showNewChatView =
    !loading &&
    messages.length === 0 &&
    (!artifactGenerations || artifactGenerations.length === 0)

  return (
    <>
      <ScrollArea className="flex-1 p-4 md:p-6" ref={scrollRef}>
        <div className="max-w-3xl mx-auto space-y-6 pb-4">
          {gaps && gaps.length > 0 && (
            <GapPanel
              visibleGaps={visibleGaps}
              openCount={unresolvedGaps.length}
              totalCount={gaps.length}
            />
          )}

          {showNewChatView && (
            <NewChatView onSelectCategory={onSelectCategory} gaps={gaps} />
          )}

          {loading && messages.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          )}

          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={cn(
                'flex gap-4 max-w-2xl',
                msg.role === 'user' ? 'ml-auto flex-row-reverse' : 'mr-auto'
              )}
            >
              <div
                className={cn(
                  'h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold mt-1 shadow-sm',
                  msg.role === 'user'
                    ? 'bg-primary/20 text-primary border border-primary/20'
                    : 'bg-secondary text-foreground border border-white/5'
                )}
              >
                {msg.role === 'user' ? 'US' : 'AI'}
              </div>
              <div
                className={cn(
                  'rounded-2xl px-5 py-3 text-sm leading-relaxed shadow-sm whitespace-pre-wrap',
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-tr-sm'
                    : 'bg-secondary text-foreground rounded-tl-sm border border-white/15'
                )}
              >
                {msg.content}
              </div>
            </motion.div>
          ))}

          {/* Agent state indicator */}
          <AnimatePresence>
            {agentState !== 'idle' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="flex items-center gap-3 max-w-2xl mr-auto"
              >
                <div className="h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center bg-secondary border border-white/5">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
                <div className="rounded-2xl px-5 py-3 bg-secondary border border-white/15 rounded-tl-sm">
                  <span className="text-sm text-muted-foreground italic">
                    {agentState === 'thinking' ? 'Thinking...' : 'Extracting insights...'}
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Artifact generation cards */}
          {artifactGenerations && artifactGenerations.length > 0 && (
            <div className="space-y-4 pt-2">
              {artifactGenerations.map((gen) => (
                <motion.div
                  key={gen.artifactId}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mr-auto"
                >
                  <ArtifactCard
                    generation={gen}
                    onDismiss={onDismissArtifact}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="p-4 bg-background border-t border-border/50">
        <div className="max-w-2xl mx-auto relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-accent-amber/20 rounded-full blur opacity-20 group-hover:opacity-40 transition duration-500" />
          {voiceState === 'idle' ? (
            <div
              className={cn(
                'relative flex bg-white dark:bg-secondary border border-slate-300 dark:border-white/20 px-4 md:px-6 shadow-lg backdrop-blur-sm transition-all duration-200',
                isComposerExpanded ? 'items-end py-3 rounded-[32px]' : 'items-center py-3 md:py-4 rounded-full'
              )}
            >
              <button
                type="button"
                className={cn(
                  'text-muted-foreground hover:text-foreground transition-colors mr-3 md:mr-4 shrink-0',
                  isComposerExpanded ? 'mb-1' : ''
                )}
                onClick={onUploadClick}
              >
                <Paperclip className="h-5 w-5" />
              </button>
              <textarea
                ref={textareaRef}
                className="flex-1 bg-transparent border-none resize-none focus:outline-none text-sm md:text-base max-h-[300px] placeholder:text-muted-foreground/70 leading-8 min-h-[24px] py-0"
                placeholder="Type a message..."
                rows={1}
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    onSend()
                  }
                }}
              />
              <div className={cn('flex items-center pl-2 shrink-0', isComposerExpanded ? 'self-end pb-0.5' : '')}>
                <button
                  type="button"
                  className="p-2 mr-1 text-muted-foreground hover:text-primary transition-colors"
                  onClick={startVoiceCapture}
                >
                  <Mic className="h-5 w-5" />
                </button>
                <Button
                  size="icon"
                  className="h-8 w-8 md:h-10 md:w-10 bg-primary hover:bg-primary/90 text-white rounded-full shadow-lg shadow-primary/20 ml-2"
                  onClick={() => onSend()}
                >
                  <Send className="h-4 w-4 md:h-5 md:w-5 ml-0.5" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="relative flex items-center bg-white dark:bg-secondary rounded-full border border-slate-300 dark:border-white/20 px-4 md:px-6 py-3 md:py-4 shadow-lg backdrop-blur-sm gap-3">
              <button
                type="button"
                className="h-8 w-8 md:h-9 md:w-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={onUploadClick}
                disabled={!onUploadClick}
              >
                <Plus className="h-5 w-5" />
              </button>

              <VoiceWaveform processing={voiceState === 'processing'} />

              <button
                type="button"
                className="h-8 w-8 md:h-9 md:w-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={cancelVoiceCapture}
                disabled={voiceState === 'processing'}
              >
                <X className="h-5 w-5" />
              </button>

              <button
                type="button"
                className="h-10 w-10 md:h-12 md:w-12 rounded-full border-2 border-primary/70 bg-background/60 dark:bg-background/40 text-primary flex items-center justify-center shrink-0"
                onClick={completeVoiceCapture}
                disabled={voiceState === 'processing'}
              >
                {voiceState === 'processing' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Check className="h-5 w-5" />
                )}
              </button>
            </div>
          )}
          <div className="text-center mt-3">
            <p className="text-[10px] text-muted-foreground">
              ContextMD can make mistakes. Please verify important information.
            </p>
            {voiceError && (
              <p className="text-[11px] text-destructive mt-1">{voiceError}</p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

function VoiceWaveform({ processing }: { processing: boolean }) {
  return (
    <div className="relative flex-1 h-9 overflow-hidden">
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t border-dashed border-slate-500/45 dark:border-white/35" />
      <div className="relative z-10 h-full flex items-center justify-center gap-1">
        {WAVEFORM_BARS.map((height, index) => (
          <motion.span
            key={`${index}-${processing ? 'processing' : 'recording'}`}
            className="w-0.5 rounded-full bg-slate-700 dark:bg-white/90"
            initial={{ height: `${Math.max(4, Math.round(height * 0.35))}px` }}
            animate={
              processing
                ? {
                    height: [
                      `${Math.max(4, Math.round(height * 0.2))}px`,
                      `${Math.max(6, Math.round(height * 0.65))}px`,
                      `${Math.max(4, Math.round(height * 0.25))}px`,
                    ],
                    opacity: [0.45, 0.95, 0.55],
                  }
                : {
                    height: [
                      `${Math.max(6, Math.round(height * 0.45))}px`,
                      `${height}px`,
                      `${Math.max(5, Math.round(height * 0.35))}px`,
                    ],
                    opacity: [0.5, 1, 0.55],
                  }
            }
            transition={{
              duration: processing ? 1.1 : 0.75,
              ease: 'easeInOut',
              repeat: Infinity,
              repeatType: 'mirror',
              delay: index * 0.03,
            }}
          />
        ))}
      </div>
    </div>
  )
}

function GapPanel({
  visibleGaps,
  openCount,
  totalCount,
}: {
  visibleGaps: ContextGap[]
  openCount: number
  totalCount: number
}) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-[#454a6d] bg-white dark:bg-[#23263b] p-3 md:p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className="h-4 w-4 text-accent-warning shrink-0" />
          <h3 className="text-sm font-semibold truncate">
            {openCount > 0 ? `${openCount} Critical Gap${openCount > 1 ? 's' : ''} Detected` : 'All Gaps Resolved'}
          </h3>
        </div>
        <span className="text-[11px] text-muted-foreground shrink-0">
          {totalCount - openCount}/{totalCount} resolved
        </span>
      </div>

      <div className="space-y-2">
        {visibleGaps.map((gap) => (
          <div
            key={gap.id}
            className="rounded-xl border border-slate-200 dark:border-[#454a6d] bg-slate-50 dark:bg-[#282941] px-3 py-2.5"
          >
            <div className="flex items-center justify-between gap-3 mb-1">
              <div className="flex items-center gap-2 min-w-0">
                {gap.status === 'in_progress' ? (
                  <Clock3 className="h-3.5 w-3.5 text-primary shrink-0" />
                ) : gap.status === 'resolved' ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-accent-success shrink-0" />
                ) : (
                  <AlertCircle className="h-3.5 w-3.5 text-accent-warning shrink-0" />
                )}
                <span className="text-xs font-medium truncate">
                    {gap.description || gap.domain.replace(/_/g, ' ')}
                </span>
              </div>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
                {gap.priority}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
              {gap.suggestedQuestion || gap.description}
            </p>
          </div>
        ))}

        {openCount > visibleGaps.length && (
          <p className="text-[11px] text-muted-foreground px-1">
            +{openCount - visibleGaps.length} more open gap{openCount - visibleGaps.length > 1 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  )
}
